import { BcmOrder } from '@shared/models/bcm-order';
import { formatOrderDisplayNumber } from '@modules/bcm/@shared/utils/invoice-position-source.util';
import { pickDefaultOrderIdForBooking } from '@modules/bcm/booking-v2/utils/booking-v2-order-link.util';

export interface BoatOrderPhotoOption {
    orderId: number;
    label: string;
    isLinkedToBoat: boolean;
}

export function isOrderLinkedToBoat(order: Pick<BcmOrder, 'boats'>, boatId: number): boolean {
    return (order.boats || []).some((boat) => boat.id === boatId);
}

export function filterOrdersForBoatPhotoLink(orders: readonly BcmOrder[], boatId: number): BcmOrder[] {
    return orders
        .filter((order) => isOrderLinkedToBoat(order, boatId))
        .sort((left, right) => compareOrdersForBoatPhotoSelection(left, right, boatId));
}

function compareOrdersForBoatPhotoSelection(left: BcmOrder, right: BcmOrder, boatId: number): number {
    const leftLinked = isOrderLinkedToBoat(left, boatId) ? 1 : 0;
    const rightLinked = isOrderLinkedToBoat(right, boatId) ? 1 : 0;

    if (leftLinked !== rightLinked) {
        return rightLinked - leftLinked;
    }

    const leftTime = left.startTime instanceof Date ? left.startTime.getTime() : 0;
    const rightTime = right.startTime instanceof Date ? right.startTime.getTime() : 0;

    if (leftTime !== rightTime) {
        return rightTime - leftTime;
    }

    return (right.id ?? 0) - (left.id ?? 0);
}

export function sortOrdersForBoatPhotoSelection(orders: readonly BcmOrder[], boatId: number): BcmOrder[] {
    return [...orders].sort((left, right) => compareOrdersForBoatPhotoSelection(left, right, boatId));
}

export function formatBoatOrderPhotoOptionLabel(
    order: Pick<BcmOrder, 'id' | 'orderNumber' | 'orderTitle' | 'clientName'>,
): string {
    const number = order.orderNumber || formatOrderDisplayNumber(order.id);
    const title = (order.orderTitle || '').trim();
    const client = (order.clientName || '').trim();
    const parts = [number];

    if (title) {
        parts.push(title);
    }

    if (client) {
        parts.push(client);
    }

    return parts.join(' – ');
}

export function buildBoatOrderPhotoOptions(orders: readonly BcmOrder[], boatId: number): BoatOrderPhotoOption[] {
    return sortOrdersForBoatPhotoSelection(orders, boatId).map((order) => ({
        orderId: order.id,
        label: formatBoatOrderPhotoOptionLabel(order),
        isLinkedToBoat: isOrderLinkedToBoat(order, boatId),
    }));
}

export function pickDefaultBoatOrderPhotoOrderId(orders: readonly BcmOrder[], boatId: number): number | null {
    const linkedOrders = filterOrdersForBoatPhotoLink(orders, boatId);

    if (linkedOrders.length) {
        return pickDefaultOrderIdForBooking(linkedOrders, boatId);
    }

    const sortedOrders = sortOrdersForBoatPhotoSelection(orders, boatId);

    return sortedOrders[0]?.id ?? null;
}
